import { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, User } from 'lucide-react';
import { Room, ReservationWithDetails } from '@/shared/types';

interface TimelineCalendarProps {
  rooms: Room[];
  reservations: ReservationWithDetails[];
  onDateRoomClick: (date: string, room: Room) => void;
  onReservationClick?: (reservation: ReservationWithDetails) => void;
}

interface CalendarDay {
  date: Date;
  dateString: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}

export default function TimelineCalendar({ 
  rooms, 
  reservations, 
  onDateRoomClick, 
  onReservationClick 
}: TimelineCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  // Gerar dias do calendário
  const generateCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (viewMode === 'week') {
      // Modo semana: 7 dias a partir da data atual
      const startDate = new Date(currentDate);
      const dayOfWeek = startDate.getDay();
      startDate.setDate(startDate.getDate() - dayOfWeek); // Começar no domingo
      
      const days: CalendarDay[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        days.push({
          date,
          dateString: date.toISOString().split('T')[0],
          isCurrentMonth: date.getMonth() === month,
          isToday: date.toDateString() === new Date().toDateString()
        });
      }
      return days;
    } else {
      // Modo mês: todos os dias do mês atual
      const lastDay = new Date(year, month + 1, 0);
      
      const days: CalendarDay[] = [];
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(year, month, day);
        days.push({
          date,
          dateString: date.toISOString().split('T')[0],
          isCurrentMonth: true,
          isToday: date.toDateString() === new Date().toDateString()
        });
      }
      return days;
    }
  };

  // Verificar se um quarto tem reserva em uma data específica
  const getRoomReservationForDate = (roomId: number, dateString: string) => {
    return reservations.find(reservation => 
      reservation.room_id === roomId &&
      dateString >= reservation.check_in_date && 
      dateString < reservation.check_out_date &&
      reservation.status !== 'cancelled'
    );
  };

  // Obter cor do status da reserva
  const getReservationColor = (reservation: ReservationWithDetails) => {
    switch (reservation.status) {
      case 'confirmed':
        return 'bg-blue-300 hover:bg-blue-400 text-blue-900';
      case 'checked_in':
        return 'bg-red-500 hover:bg-red-600 text-white';
      case 'checked_out':
        return 'bg-gray-500 hover:bg-gray-600 text-white';
      default:
        return 'bg-gray-400 hover:bg-gray-500 text-white';
    }
  };

  // Navegar entre meses/semanas
  const navigateCalendar = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const calendarDays = generateCalendarDays();

  // Formatação do período atual
  const formatPeriod = () => {
    if (viewMode === 'week') {
      const firstDay = calendarDays[0]?.date;
      const lastDay = calendarDays[6]?.date;
      if (firstDay && lastDay) {
        return `${firstDay.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - ${lastDay.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
      }
    }
    return currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header do Calendário */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Calendário de Ocupação - {formatPeriod()}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'month' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Mês
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'week' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Semana
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateCalendar('prev')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Hoje
            </button>
            <button
              onClick={() => navigateCalendar('next')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-300 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Reservado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Ocupado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-500 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Check-out Feito</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-white border-2 border-dashed border-gray-400 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Disponível</span>
          </div>
        </div>
      </div>

      {/* Timeline do Calendário */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header com os dias */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {/* Coluna dos quartos (fixa) */}
            <div className="w-48 flex-shrink-0 bg-gray-50 dark:bg-gray-700/50 border-r border-gray-200 dark:border-gray-700">
              <div className="h-12 flex items-center justify-center font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700">
                Quartos
              </div>
            </div>
            
            {/* Colunas dos dias */}
            <div className="flex flex-1">
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  className={`min-w-[120px] flex-1 border-r border-gray-200 dark:border-gray-700 ${
                    day.isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="h-12 flex flex-col items-center justify-center text-center border-b border-gray-200 dark:border-gray-700">
                    <div className={`text-sm font-medium ${
                      day.isToday 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {day.date.toLocaleDateString('pt-BR', { weekday: 'short' })}
                    </div>
                    <div className={`text-xs ${
                      day.isToday 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {day.date.getDate().toString().padStart(2, '0')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Linhas dos quartos */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {rooms.map((room) => (
              <div key={room.id} className="flex hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                {/* Informações do quarto */}
                <div className="w-48 flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-700/50 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      room.status === 'available' ? 'bg-green-500' :
                      room.status === 'maintenance' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`}></div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {room.room_number}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {room.room_type}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {room.max_occupancy} pessoas
                      </div>
                    </div>
                  </div>
                </div>

                {/* Células dos dias para este quarto */}
                <div className="flex flex-1">
                  {calendarDays.map((day, dayIndex) => {
                    const reservation = getRoomReservationForDate(room.id, day.dateString);
                    const isFirstDay = reservation && day.dateString === reservation.check_in_date;
                    const isLastDay = reservation && day.dateString === new Date(new Date(reservation.check_out_date).getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                    
                    return (
                      <div
                        key={dayIndex}
                        className="min-w-[120px] flex-1 h-16 border-r border-gray-200 dark:border-gray-700 relative"
                      >
                        {reservation ? (
                          // Célula com reserva
                          <div
                            onClick={() => onReservationClick?.(reservation)}
                            className={`absolute inset-1 cursor-pointer transition-all ${getReservationColor(reservation)} flex items-center justify-center group ${
                              isFirstDay ? 'rounded-l-md' : 'rounded-l-none'
                            } ${
                              isLastDay ? 'rounded-r-md' : 'rounded-r-none'
                            } ${
                              !isFirstDay && !isLastDay ? 'rounded-none' : ''
                            }`}
                            title={`${reservation.guest_name} - Check-in: ${new Date(reservation.check_in_date).toLocaleDateString('pt-BR')} - Check-out: ${new Date(reservation.check_out_date).toLocaleDateString('pt-BR')} - Status: ${reservation.status}`}
                          >
                            {isFirstDay && (
                              <div className="flex items-center space-x-1 text-xs font-medium">
                                <User className="w-3 h-3" />
                                <span className="truncate max-w-[80px]">
                                  {reservation.guest_name}
                                </span>
                              </div>
                            )}
                            {!isFirstDay && !isLastDay && (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white/30 rounded-full"></div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Célula disponível
                          <button
                            onClick={() => onDateRoomClick(day.dateString, room)}
                            className="w-full h-full flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group border-2 border-dashed border-transparent hover:border-gray-300 dark:hover:border-gray-600 rounded"
                            title={`Criar reserva para ${room.room_number} em ${day.date.toLocaleDateString('pt-BR')}`}
                          >
                            <Plus className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer com estatísticas */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Total de {rooms.length} quartos
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>
                {reservations.filter(r => r.status === 'checked_in' && calendarDays.some(d => d.dateString >= r.check_in_date && d.dateString < r.check_out_date)).length} ocupados
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-blue-300 rounded"></div>
              <span>
                {reservations.filter(r => r.status === 'confirmed' && calendarDays.some(d => d.dateString >= r.check_in_date && d.dateString < r.check_out_date)).length} reservados
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
